import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import app from "../../configs/FirebaseConfig";
import { FIRESTORE_USERS_TABLE } from "../../constants/FirestoreConstant";
import { useAuth } from "./useAuth";

/**
 * Eén bron voor de `users`-collectie — bewust NIET via useFirestoreConnect.
 *
 * Waarom niet: redux-firestore kiest per snapshot tussen één bulk-actie en één
 * actie per document (`utils/query.js`, dispatchListenerResponse):
 *
 *     if (docChanges && docChanges.length < docData.size) { per document }
 *     else { bulk }
 *
 * Die heuristiek gaat hier altijd de verkeerde kant op. De AuthProvider
 * (./useAuth.js) zet het eigen profieldocument `users/{uid}` al apart in de
 * Firestore-cache, dus in de eerste snapshot van de collectie is dat ene
 * document géén "change": `docChanges.length` is 2331 en `size` 2332. Eén
 * gecachet document kantelt daarmee de hele collectie naar het per-document
 * pad — 2331 losse dispatches, elk met een volledige array-kopie (O(n²)) en een
 * React-renderronde erachteraan.
 *
 * Gemeten op de ontwikkelomgeving met 2332 gebruikers: ~45 seconden waarin de
 * hoofdthread niet reageerde, dus geen enkele klik werd verwerkt. Met een eigen
 * onSnapshot is het één callback per wijziging. Zie docs/cms-performance.md.
 *
 * Realtime blijft dus gewoon werken — dit is geen eenmalige get().
 *
 * `undefined` zolang er nog niets binnen is; dat verschil met een lege lijst
 * blijft bewaard, zodat "nog aan het laden" en "niets gevonden" uit elkaar te
 * houden zijn.
 */
const UsersContext = createContext(undefined);

export const UsersProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [users, setUsers] = useState(undefined);

  useEffect(() => {
    if (!isAuthenticated) {
      setUsers(undefined);
      return undefined;
    }
    // De regels laten een ongefilterde read toe voor elk CMS-account
    // (allow read: if hasCmsRole()); landfiltering gebeurt in de UI, zie
    // useScopedCustomers.
    const unsubscribe = app
      .firestore()
      .collection(FIRESTORE_USERS_TABLE)
      .onSnapshot(
        (snap) => setUsers(snap.docs.map((doc) => ({ ...doc.data(), id: doc.id }))),
        (err) => {
          console.error("Kon de gebruikers niet laden", err);
          setUsers([]);
        },
      );
    return unsubscribe;
  }, [isAuthenticated]);

  return <UsersContext.Provider value={users}>{children}</UsersContext.Provider>;
};

export const useAllUsers = () => useContext(UsersContext);

/** De webshopklanten. Vervangt de oude store-sleutel `webshopUsers`. */
export const useWebshopUsers = () => {
  const users = useAllUsers();
  return useMemo(() => (Array.isArray(users) ? users.filter((user) => user?.role === "webshopUser") : users), [users]);
};

/** Verkopers. Vervangt de oude store-sleutel `salesPersons`. */
export const useSalesPersons = () => {
  const users = useAllUsers();
  return useMemo(() => (Array.isArray(users) ? users.filter((user) => user?.isSalesPerson === true) : users), [users]);
};

export default useAllUsers;
